import { RegistrationMode } from '@easeltv/client-sdk';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useConfig } from '../hooks/useConfig/useConfig';
import { useAuth } from '../providers/AuthProvider/AuthProvider';

/**
 * AuthRoute
 *
 * Only register if the RegistrationMode is not 'disabled' or 'invite-only'
 * Redirect users if they are already logged in
 */
export const AuthRoute = ({ children }: PropsWithChildren) => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const initialIsAuthenticated = useRef(isAuthenticated);

    const {
        registration: { mode },
    } = useConfig();

    useEffect(() => {
        if (initialIsAuthenticated.current) {
            navigate('/', { replace: true });
        }
    }, [navigate]);

    switch (mode) {
        case RegistrationMode.Disabled:
        case RegistrationMode.InviteOnly:
            return <Navigate replace to="/404" />;

        default:
            return children;
    }
};

/**
 * RedirectIfUnAuth
 *
 * Redirect if the user is unauthenticated
 */
const RedirectIfUnAuth = ({ children }: PropsWithChildren) => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const initialIsAuthenticated = useRef(isAuthenticated);

    useEffect(() => {
        if (!initialIsAuthenticated.current) {
            navigate('/signin', { replace: true });
        }
    }, [navigate]);

    return children;
};

interface ProtectedRouteProps {
    force?: boolean;
}

/**
 * ProtectedRoute
 *
 * Routes that when appropriate should be hidden unless logged in
 */
export const ProtectedRoute = ({
    children,
    force = false,
}: PropsWithChildren<ProtectedRouteProps>) => {
    const {
        registration: { mode },
    } = useConfig();

    switch (mode) {
        case RegistrationMode.InviteOnly:
        case RegistrationMode.Required:
            return <RedirectIfUnAuth>{children}</RedirectIfUnAuth>;

        default:
            return force ? (
                <RedirectIfUnAuth>{children}</RedirectIfUnAuth>
            ) : (
                children
            );
    }
};
